import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {
  CloudUploadOutlined,
  CloseOutlined,
  FileOutlined
} from '@ant-design/icons';

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/zip',
  'application/x-rar-compressed',
  'application/x-7z-compressed'
];

const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv', '.zip', '.rar', '.7z'];

export const FileUploadField = ({
  field,
  value = [],
  onChange,
  onRemove,
  error,
  uploading,
  maxFiles
}) => {
  const [validationError, setValidationError] = useState('');

  const validateFile = (file) => {
    console.log('Yüklenen dosya:', file.name, 'MIME:', file.type, 'Uzantı:', file.name.split('.').pop());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const isValidExtension = ALLOWED_FILE_EXTENSIONS.includes(fileExtension);
    const isValidType = ALLOWED_FILE_TYPES.includes(file.type);

    if (!isValidExtension && !isValidType) {
      return `Geçersiz dosya formatı: ${file.name}. Desteklenen formatlar: ${ALLOWED_FILE_EXTENSIONS.join(', ')}`;
    }

    if (file.size > 1024 * 1024 * 1024) { // 1GB
      return `Dosya boyutu çok büyük: ${file.name}. Maksimum dosya boyutu: 1GB`;
    }

    return '';
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    let hasError = false;
    let errorMessage = '';

    // Dosya sayısı kontrolü
    if (value.length + files.length > maxFiles) {
      setValidationError(`En fazla ${maxFiles} dosya yükleyebilirsiniz`);
      return;
    }

    // Her dosyayı kontrol et
    for (const file of files) {
      const validationResult = validateFile(file);
      if (validationResult) {
        hasError = true;
        errorMessage = validationResult;
        break;
      }
    }

    if (hasError) {
      setValidationError(errorMessage);
      return;
    }

    setValidationError('');
    onChange(files);
  };

  return (
    <FormControl error={!!error || !!validationError} required={field.required} fullWidth className="form-preview-file-field">
      <Box className="form-preview-file-upload">
        <Button
          variant="outlined"
          component="label"
          size="medium"
          startIcon={<CloudUploadOutlined />}
          disabled={uploading}
          sx={{
            width: '100%',
            height: '120px',
            border: '2px dashed',
            borderColor: 'primary.main',
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.dark'
            }
          }}
        >
          <Stack spacing={1} alignItems="center">
            <CloudUploadOutlined sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="body2" color="primary">
              {uploading ? 'Yükleniyor...' : 'Dosya Yüklemek İçin Tıklayın'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              veya sürükleyip bırakın
            </Typography>
          </Stack>
          <input
            type="file"
            hidden
            multiple
            accept={ALLOWED_FILE_EXTENSIONS.join(',')}
            onChange={handleFileChange}
          />
        </Button>

      {/*   <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Desteklenen formatlar: PDF, Word, Excel, PowerPoint, TXT, CSV, ZIP, RAR, 7Z
          <br />
          Maksimum dosya sayısı: {maxFiles}
          <br />
          Maksimum dosya boyutu: 1GB
        </Typography>
 */}
        {value.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Yüklenen Dosyalar ({value.length}/{maxFiles}):
            </Typography>
            <Stack spacing={1}>
              {value.map((file, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FileOutlined style={{ fontSize: 24, color: '#666' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(file.size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => onRemove(index)}
                    disabled={uploading}
                    sx={{
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.lighter'
                      }
                    }}
                  >
                    <CloseOutlined fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {(error || validationError) && (
          <FormHelperText error sx={{ mt: 1 }}>
            {error || validationError}
          </FormHelperText>
        )}
      </Box>
    </FormControl>
  );
}; 